import React, { useState, useContext } from 'react';
import { useTranslate } from 'react-admin';
import { IconButton, Collapse, TextField, Button, Autocomplete, InputAdornment, Select, MenuItem } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { Editor, Transforms, Element, Node, Path } from 'slate';
import { ImageButton, InsertInput, ParamContext } from './slateComponents';

const getParentsToRoot = (editor, path) => {
  const ancestors = [];

  let currentPath = path;
  while (currentPath?.length > 0) {
    try {
      const [parentNode, parentPath] = Editor.parent(editor, currentPath);
      ancestors.push({ node: parentNode, path: parentPath });
      currentPath = parentPath;
    } catch (e) {
      break; // Reached the root
    }
  }

  return ancestors;
};

const type_mapping = {
  'root': '',
  'paragraph': 'p',
  'link': 'a',
  'bulletedlist': 'ul',
  'headingone': 'h1',
  'headingtwo': 'h2',
  'headingthree': 'h3',
  'headingfour': 'h4',
  'headingfive': 'h5',
  'headingsix': 'h6',
  'image': 'img',
  'listitem': 'li',
  'numberedlist': 'ol',
  'table': 'table',
  'tablebody': 'tbody',
  'tablerow': 'tr',
  'tablecell': 'td',
  'tablehead': 'th',
  'font': 'font',
  'hr': 'hr',
  'br': 'br',
  'div': 'div',
  'span': 'span',
  'pre': 'pre',
  'text': 'txt',
  'var': 'var',
}

const ParentTree = ({ editor, selectedPath }) => {
  const ancestors = getParentsToRoot(editor, selectedPath).reverse().slice(1)

  return (
    <div>
      {ancestors.map(({ node, path }, index) => {
        const fullTypePath = ancestors
          .slice(0, index + 1)
          .map(({ node }, idx) => type_mapping[node.type || (idx===0?'':'text')])
          .join(' > ');

        return (
          <ExpandableNode 
            editor={editor}
            node={node} 
            key={path.join('-')} 
            path={path} 
            fullTypePath={fullTypePath} 
            last={ancestors.length===index+1}
            depth={0}
          />
        )
      })}
      {
        <InsertInput editor={editor} node={editor.children} />
      }
    </div>
  );
};

const ExpandableNode = ({ editor, node, path, fullTypePath, last, depth }) => {
  const [open, setOpen] = useState(last);
  const tr = useTranslate();
  //console.log(path)

  const handleAttributeChange = (key, newValue) => {
    console.log([key, newValue, path])
    var parsedValue = newValue
    if(key === 'style'){
      try {
        parsedValue = JSON.parse(newValue); // attempt to parse JSON
      } catch (e) {
        console.log(['catch', e])
        // Invalid JSON — could show an error or keep the field red
      }
    }
    Transforms.setNodes(
      editor,
      { [key]: parsedValue },
      { at: path }
    );
  };

  return (
    <>
    <div style={{border: '1px solid #AAA', margin: '.1em', marginLeft: depth+'.15em', borderRadius: '5px'}}>
      <IconButton size="small" sx={{ pt: '0.1em', pb: '0.1em'}} onClick={() => setOpen(!open)}>
        {open ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
      <span style={{ fontWeight: 'bold', border: '1 solid #AAA'}}>{tr('bw.set.bookingsheet.'+(node.type || 'text'))}</span>
    </div>
    <div style={{ marginLeft: depth+'.15em' }}>
      <Collapse in={open}>
        {node.type!=='image' && Object.entries(node)
          .filter(([key]) => key !== 'children' && key !== 'siblings' && key !== 'type')
          .map(([key, value]) => (
            <AttributeInput
              key={'ai'+key}
              akey={key}
              value={value}
              size="small"
              margin="none"
              fullWidth
              handleChange={handleAttributeChange}
            />
          ))}
        {node.type==='image' && 
          <ImageInput 
            node={node}
            editor={editor} 
            handleChange={handleAttributeChange} 
          />
        }
        { <div style={{ display: 'flex' }} >
            <InsertInput editor={editor} node={node} path={path} /> 
            <DeleteInput editor={editor} node={node} fullTypePath={fullTypePath} path={path} /> 
          </div> }
        {open && Array.isArray(node.children) && node.children.map((child, index) => {
          const childPath = [...path, index];
          const [childNode] = Editor.node(editor, childPath)
          //const childTypePath = `${fullTypePath} > ${type_mapping[childNode.type || 'text']}`;
          const childTypePath = `${type_mapping[childNode.type || 'text']}`;
          if (child.var) return (
            <VarNode
              editor={editor}
              node={childNode}
              key={childPath.join('-')}
              path={childPath}
              fullTypePath={childTypePath}
              depth={depth+1}
            />
          );
          if (!Element.isElement(child)) return null;
          return (
            <ExpandableNode
              editor={editor}
              node={childNode}
              key={childPath.join('-')}
              path={childPath}
              fullTypePath={childTypePath}
              depth={depth+1}
            />
          );
        })}
      </Collapse>
    </div>
    </>
  );
};

const VarNode = ({ editor, node, path, fullTypePath, last, depth }) => {
  const [open, setOpen] = useState(last);
  const { vars } = useContext(ParamContext)

  const handleAttributeChange = (key, newValue) => {
    console.log([key, newValue, path])
    
    Transforms.insertText(editor, newValue.label, { at: path } );
  };

  return (
    <>
    <div style={{border: '1px solid #AAA', margin: '.1em', marginLeft: depth+'.15em', borderRadius: '5px'}}>
      <IconButton size="small" sx={{ pt: '0.1em', pb: '0.1em'}} onClick={() => setOpen(!open)}>
        {open ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
      <span style={{ fontWeight: 'bold', border: '1 solid #AAA'}}>Variable - {node.text}</span>
    </div>
    <Collapse in={open}>
      <div style={{ marginLeft: depth+'.15em' }}>
        <Autocomplete 
          label="Variable" 
          value={node.text}
          options={vars} 
          onChange={(e, val) => {if(val)handleAttributeChange('text', val)}} 
          renderInput={(params) => <TextField {...params} label="Variable" />}
          isOptionEqualToValue={(option, val) => option.id === val}
          clearOnEscape
        />
      </div>
    </Collapse>
    </>
  );
};

const ImageInput = ({editor, handleChange, node}) => {
  const handleImage = (ed, value) => {
    handleChange("url", value)
  }
  const props = {size:"small", margin: "none", fullWidth: true}
  return (
    <>
      <TextField {...props} key="iiurl" label="url" value={node.url} 
        onChange={(e) => handleChange("url", e.target.value)} 
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ImageButton editor={editor} handleImage={handleImage} />
            </InputAdornment>
          ),
        }}
      />
      <TextField {...props} key="iiheight" label="height" value={node.height} onChange={(e) => handleChange("height", e.target.value)} />
      <TextField {...props} key="iiwidth" label="width" value={node.width} onChange={(e) => handleChange("width", e.target.value)} />
      <TextField {...props} key="iialt" label="alt" value={node.alt} onChange={(e) => handleChange("alt", e.target.value)} />
    </>
  )
}

const AttributeInput = ({ akey, value, handleChange, ...props}) => {
  switch(akey){
    case 'type':
      return <div className="bold-leaf" style={{margin: '.5em'}}>{value}</div>
    case 'style':
      return <TextField {...props} key={akey} label={akey} value={typeof value === 'object'?JSON.stringify(value):value} onChange={(e) => {e.preventDefault();handleChange(akey, e.target.value)}} />
    case 'face':
      return <Select id={"font-select"+akey} value={value} label="Font" onChange={(e) => {handleChange(akey, e.target.value)}} sx={{width:'100%'}}>
              <MenuItem value={'Courier'}>Courier</MenuItem>
              <MenuItem value={'Helvetica'}>Helvetica</MenuItem>
              <MenuItem value={'Monospace'}>DejaVu Sans Mono</MenuItem>
              <MenuItem value={'Sans'}>DevaVu Sans</MenuItem>
              <MenuItem value={'Serif'}>DejaVu Serif</MenuItem>
              <MenuItem value={'Times'}>Times</MenuItem>
            </Select>
    default:
      return <TextField {...props} key={akey} label={akey} value={value} onChange={(e) => {e.preventDefault();handleChange(akey, e.target.value)}} />
  }
}

const DeleteInput = ({editor, fullTypePath, node, path}) => {
  const tr = useTranslate();
  const [open, setOpen] = useState(false);
  const noDelEmptyNodes = ['tablebody', 'tablerow', 'tablecell', 'numberedlist', 'bulletedlist']
  
  if(noDelEmptyNodes.includes(node?.type)){
    const siblingIndex = path[path.length - 1] + 1
      const parentPath = Path.parent(path)

      try {
        const siblingPath = [...parentPath, siblingIndex]
        Node.get(editor, siblingPath)
        return true
      } catch {
        return false
      }
  }

  //console.log([node,path])
  return (
    <div>
      <Button sx={{ color: 'red' }} onClick={() => setOpen(!open)}>{tr('bw.set.bookingsheet.delete')}</Button>

      {open && (
        <div style={{ position: 'absolute', background: '#fff', border: '1px solid #ccc', padding: '8px', zIndex: '100' }}>
          <div>{tr('bw.set.bookingsheet.deleteconfirm')}</div><div>{fullTypePath} - {node?.text}</div>
          <Button 
            onClick={() => {
              setOpen(!open)
              console.log(path)
              Transforms.removeNodes(editor, { at: { anchor: {path: path, offset: 0}, focus: {path: path, offset: 0}}}) 
            }}>
            {tr('bw.set.bookingsheet.yes')}
          </Button>
          <Button onClick={() => setOpen(!open)}>{tr('bw.set.bookingsheet.no')}</Button>
        </div>
      )}
    </div>
  );
}

export { getParentsToRoot, ParentTree }
